/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Trash2, Search, Users, Mail, ShieldCheck, Trophy } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Layout from "@/components/Layout";
import TablePagination from "@/components/TablePagination";
import { toast } from "react-toastify";
import { useUsers, useDeleteUser } from "@/hooks/api/useUsers";
import type { AdminUser, AdminUserListResponse } from "@/types/api.types";
import { useNavigate } from "react-router-dom";

const PROVIDER_COLORS: Record<string, string> = {
  email: "bg-gray-100 text-gray-700",
  google: "bg-blue-50 text-blue-700",
  apple: "bg-gray-800 text-white",
  oauth: "bg-purple-50 text-purple-700",
};

const PROVIDER_ICONS: Record<string, string> = {
  email: "✉️",
  google: "G",
  apple: "",
  oauth: "🔗",
};

function AvatarCell({ user }: { user: AdminUser }) {
  const initials = user.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex items-center gap-3">
      {user.avatar ? (
        <img
          src={user.avatar}
          alt={user.name}
          className="w-8 h-8 rounded-full object-cover border border-gray-200"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
          {initials}
        </div>
      )}
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
        {user.username && (
          <p className="text-xs text-gray-400 truncate">@{user.username}</p>
        )}
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  sub,
  color,
}: {
  icon: any;
  label: string;
  value: string | number;
  sub?: string;
  color: string;
}) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 flex items-center gap-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function UserManagement() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [authProvider, setAuthProvider] = useState<string | undefined>();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useUsers({
    page,
    limit,
    search: debouncedSearch || undefined,
    authProvider,
  });

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser();

  const response = data as AdminUserListResponse | undefined;
  const users = response?.users ?? [];
  const pagination = response?.pagination;

  const handleDelete = (user: AdminUser) => {
    if (
      !confirm(
        `Delete "${user.name}" (${user.email})?\n\nThis will permanently delete their account, team, and all fantasy data. This cannot be undone.`,
      )
    )
      return;

    deleteUser(user.id, {
      onSuccess: () => toast.success(`User "${user.name}" deleted`),
      onError: (e: any) =>
        toast.error(e.response?.data?.message || "Failed to delete user"),
    });
  };

  const handleProviderFilter = (val: string) => {
    setAuthProvider(val === "all" ? undefined : val);
    setPage(1);
  };

  // Count providers from current page (approx stats)
  const emailCount = users.filter((u) => u.authProvider === "email").length;
  const oauthCount = users.filter((u) => u.authProvider !== "email").length;

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-500 mt-1">
            View and manage all registered users
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/fantasy-leaderboard")}
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
        >
          <Trophy className="h-4 w-4 mr-2" />
          View Leaderboard
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Users}
          label="Total Users"
          value={pagination?.total ?? "—"}
          color="bg-blue-50 text-blue-600"
        />
        <StatCard
          icon={Mail}
          label="Email sign-ups"
          value={pagination ? `${emailCount} shown` : "—"}
          sub="on this page"
          color="bg-gray-100 text-gray-600"
        />
        <StatCard
          icon={ShieldCheck}
          label="OAuth sign-ups"
          value={pagination ? `${oauthCount} shown` : "—"}
          sub="Google / Apple"
          color="bg-purple-50 text-purple-600"
        />
        <StatCard
          icon={Users}
          label="Page"
          value={pagination ? `${pagination.page} / ${pagination.totalPages}` : "—"}
          sub={`${limit} per page`}
          color="bg-green-50 text-green-600"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email or username…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={authProvider ?? "all"} onValueChange={handleProviderFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All providers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All providers</SelectItem>
            <SelectItem value="email">✉️ Email</SelectItem>
            <SelectItem value="google">G Google</SelectItem>
            <SelectItem value="apple"> Apple</SelectItem>
          </SelectContent>
        </Select>
        {(debouncedSearch || authProvider) && (
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-500 text-xs"
            onClick={() => {
              setSearch("");
              setAuthProvider(undefined);
              setPage(1);
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">User</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Provider</TableHead>
                  <TableHead className="font-semibold">Teams</TableHead>
                  <TableHead className="font-semibold">Joined</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-gray-400">
                      {debouncedSearch || authProvider
                        ? "No users match your filters"
                        : "No users registered yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="hover:bg-gray-50">
                      {/* Avatar + Name */}
                      <TableCell>
                        <AvatarCell user={user} />
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <span className="text-sm text-gray-600">{user.email}</span>
                      </TableCell>

                      {/* Auth Provider */}
                      <TableCell>
                        <Badge
                          className={`text-xs font-medium ${
                            PROVIDER_COLORS[user.authProvider] ?? PROVIDER_COLORS.oauth
                          }`}
                        >
                          {PROVIDER_ICONS[user.authProvider] ?? "🔗"}{" "}
                          {user.authProvider.charAt(0).toUpperCase() +
                            user.authProvider.slice(1)}
                        </Badge>
                      </TableCell>

                      {/* Teams */}
                      <TableCell>
                        <span className="text-sm text-gray-600">
                          {user._count?.teams ?? 0} team{user._count?.teams !== 1 ? "s" : ""}
                        </span>
                      </TableCell>

                      {/* Joined */}
                      <TableCell>
                        <span className="text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(`/fantasy-leaderboard?userId=${user.id}`)
                            }
                            title="View rank on leaderboard"
                            className="text-blue-600 hover:bg-blue-50 text-xs"
                          >
                            <Trophy className="h-3.5 w-3.5 mr-1" />
                            Rank
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(user)}
                            disabled={isDeleting}
                            title="Delete user"
                            className="text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <TablePagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                pageSize={pagination.limit}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setLimit(size);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Help text */}
      <p className="text-xs text-gray-400 mt-3 text-center">
        Users register through the mobile app. Admins can search, filter, and remove accounts only.
      </p>
    </Layout>
  );
}
